import { useState } from "react";
import { ActivityIndicator, Pressable, TextInput, KeyboardAvoidingView, Platform } from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { SafeAreaView } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { colors, radius, font } from "@/lib/theme";

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const Content = styled.View`
  flex: 1;
  padding: 24px;
  max-width: 560px;
  width: 100%;
  align-self: center;
`;

const Title = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  margin-bottom: 24px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const FieldHint = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-bottom: 8px;
`;

const Input = styled(TextInput)`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding: 14px 16px;
  font-size: 16px;
  color: ${colors.ink};
  margin-bottom: 16px;
`;

const MessageInput = styled(TextInput)`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding: 16px;
  font-size: 16px;
  color: ${colors.ink};
  min-height: 140px;
  text-align-vertical: top;
`;

const CharCount = styled.Text<{ over?: boolean }>`
  font-size: 12px;
  color: ${(p: { over?: boolean }) => (p.over ? colors.danger : colors.inkMuted)};
  text-align: right;
  margin-top: 4px;
`;

const SubmitButton = styled(Pressable)<{ disabled?: boolean }>`
  background-color: ${(p: { disabled?: boolean }) => (p.disabled ? colors.inkMuted : colors.brand)};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin-top: 20px;
`;

const SubmitText = styled.Text`
  color: ${colors.white};
  font-size: 16px;
  font-weight: ${font.bold};
`;

const BackLink = styled(Pressable)`
  align-items: center;
  padding: 16px;
`;

const BackLinkText = styled.Text`
  font-size: 14px;
  color: ${colors.brand};
  font-weight: ${font.semibold};
`;

const SuccessContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const SuccessEmoji = styled.Text`
  font-size: 48px;
  margin-bottom: 16px;
`;

const SuccessTitle = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 8px;
`;

const SuccessBody = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  text-align: center;
  line-height: 22px;
`;

const MAX_CHARS = 2000;

export default function AuthHelpScreen() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = message.trim().length > 0 && email.trim().length > 0 && message.length <= MAX_CHARS && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);

    try {
      await addDoc(collection(db, "helpRequests"), {
        message: message.trim(),
        email: email.trim().toLowerCase(),
        userId: null,
        clubId: null,
        clubName: null,
        platform: Platform.OS,
        source: "auth",
        createdAt: Timestamp.now(),
        status: "new",
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Help submit error:", err);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Container>
        <SuccessContainer>
          <SuccessEmoji>🏏</SuccessEmoji>
          <SuccessTitle>Message sent</SuccessTitle>
          <SuccessBody>
            We'll get back to you at {email} as soon as we can.
          </SuccessBody>
          <BackLink onPress={() => router.back()}>
            <BackLinkText>Back to sign in</BackLinkText>
          </BackLink>
        </SuccessContainer>
      </Container>
    );
  }

  return (
    <Container>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <KeyboardAwareScroll contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
          <Content>
            <Title>Get help</Title>
            <Subtitle>
              Having trouble signing in or signing up? Let us know and we'll help you out.
            </Subtitle>

            <Label>Your email</Label>
            <FieldHint>So we can get back to you</FieldHint>
            <Input
              placeholder="you@email.com"
              placeholderTextColor={colors.inkMuted}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              value={email}
              onChangeText={setEmail}
            />

            <Label>Your message</Label>
            <MessageInput
              placeholder="Describe your issue..."
              placeholderTextColor={colors.inkMuted}
              multiline
              maxLength={MAX_CHARS + 100}
              value={message}
              onChangeText={setMessage}
              textAlignVertical="top"
            />
            <CharCount over={message.length > MAX_CHARS}>
              {message.length}/{MAX_CHARS}
            </CharCount>

            <SubmitButton onPress={handleSubmit} disabled={!canSubmit}>
              {submitting ? (
                <ActivityIndicator color={colors.white} size="small" />
              ) : (
                <SubmitText>Send message</SubmitText>
              )}
            </SubmitButton>

            <BackLink onPress={() => router.back()}>
              <BackLinkText>Back to sign in</BackLinkText>
            </BackLink>
          </Content>
        </KeyboardAwareScroll>
      </KeyboardAvoidingView>
    </Container>
  );
}
